import {
  LayoutDashboard, Map, Bell, Users, Truck, ShieldCheck,
  Star, FileBarChart2, Calendar as CalendarIcon, Route, ClipboardList, Receipt, Banknote, Scale
} from 'lucide-react';
import type { UserFeatures, UserRole } from '../../types/user';
import { NAV_ROUTES } from '../../utils/constants';

export interface NavItem {
  label: string;
  icon: React.ReactNode;
  to: string;
  roles: UserRole[];
  /** FE-35 (ADR-034): el ítem solo existe si el despliegue tiene ese módulo (`user.features`). */
  feature?: keyof UserFeatures;
}

// Fuente única para Sidebar (desktop) y MobileNav (bottom nav): antes vivía
// solo en Sidebar.tsx y MobileNav habría necesitado su propia copia.
export const NAV_ITEMS: NavItem[] = [
  // PRIMERO a propósito (issue #228). La bottom nav solo pinta como tabs los
  // cuatro primeros ítems visibles del rol (`MobileNav.tsx`, MAX_PRIMARY_ITEMS)
  // y el resto los esconde en «Más»: estando al final, el panel principal del
  // Operador —donde inicia y finaliza el viaje, el trabajo del día— quedaba
  // dentro de «Más». También es su `roleHome` (`AppRouter.tsx`), así que el
  // orden del menú y el destino del login dicen ahora lo mismo.
  //
  // Solo lo ve el Operador, así que adelantarlo no reordena el menú de nadie más.
  { label: 'Mi Desempeño',   icon: <Star size={20} />,            to: NAV_ROUTES.OPERATOR,   roles: ['operator'] },
  { label: 'Dashboard',      icon: <LayoutDashboard size={20} />, to: NAV_ROUTES.DASHBOARD,  roles: ['executive', 'admin'] },
  { label: 'Monitoreo',      icon: <Map size={20} />,             to: NAV_ROUTES.MONITORING, roles: ['supervisor', 'admin'] },
  { label: 'Alertas',        icon: <Bell size={20} />,            to: NAV_ROUTES.ALERTS,     roles: ['supervisor', 'admin'] },
  { label: 'Conductores',    icon: <Users size={20} />,           to: NAV_ROUTES.DRIVERS,    roles: ['supervisor', 'executive', 'admin'] },
  { label: 'Vehículos',      icon: <Truck size={20} />,           to: NAV_ROUTES.VEHICLES,   roles: ['supervisor', 'executive', 'admin'] },
  { label: 'Viajes',         icon: <Route size={20} />,           to: NAV_ROUTES.TRIPS,      roles: ['supervisor', 'executive', 'admin'] },
  // Las novedades las LEEN los cuatro roles: `GET /logbook-entries` responde 200
  // al Ejecutivo (consolidado) y al Operador (solo las suyas). Quién puede
  // REGISTRAR o CORREGIR es otra cosa y lo decide `utils/logbook.ts`.
  { label: 'Novedades',      icon: <ClipboardList size={20} />,   to: NAV_ROUTES.LOGBOOK,    roles: ['operator', 'supervisor', 'executive', 'admin'] },
  // Los gastos los LEEN los cuatro roles, igual que las novedades: `GET
  // /expenses` responde 200 al Ejecutivo (consolidado) y al Operador (solo los
  // suyos). Quién puede REGISTRAR o CORREGIR lo decide `utils/expenses.ts`.
  //
  // Es también la superficie desde la que el Operador registra desde el móvil:
  // no hay FAB nuevo a propósito — «Mi Desempeño» ya tiene el SOS de pánico
  // (FE-17) y el de novedad (FE-13), y un tercer botón flotante repetiría el
  // choque que resolvió el issue #72.
  { label: 'Gastos',         icon: <Receipt size={20} />,         to: NAV_ROUTES.EXPENSES,   roles: ['operator', 'supervisor', 'executive', 'admin'] },
  // FE-32 — los ingresos NO siguen el reparto de los gastos. El Operador queda
  // fuera desde el #225 (`ActionViewRevenue`, ADR-030 §3): el importe que se
  // cobra al cliente es de los cargos superiores. Él digita el peso al iniciar
  // el viaje y sube el manifiesto, y el core crea el ingreso en su nombre
  // (ADR-032 §2) sin enseñarle la cifra.
  { label: 'Ingresos',       icon: <Banknote size={20} />,        to: NAV_ROUTES.REVENUES,   roles: ['supervisor', 'executive', 'admin'], feature: 'finance' },
  { label: 'Calendario',     icon: <CalendarIcon size={20} />,    to: NAV_ROUTES.CALENDAR,   roles: ['operator', 'supervisor', 'executive', 'admin'] },
  // FE-32 — el resultado (ingresos − gastos). El Operador no lo ve: el servidor
  // responde 403 a `/finance/summary` (ADR-030 §3).
  { label: 'Finanzas',       icon: <Scale size={20} />,           to: NAV_ROUTES.FINANCE,    roles: ['supervisor', 'executive', 'admin'], feature: 'finance' },
  { label: 'Reportes',       icon: <FileBarChart2 size={20} />,   to: NAV_ROUTES.REPORTS,    roles: ['supervisor', 'executive', 'admin'] },
  // 'supervisor' ve una versión acotada de esta misma pantalla: solo la
  // pestaña Usuarios, y solo para dar de alta/editar Operadores de su propia
  // sub-unidad (issue #77 — PRODUCT.md §4.8 "crear/eliminar Operador: Sí, su
  // sub-unidad"). AdminPage.tsx decide qué pestañas mostrar según el rol.
  // 'executive' entra desde FE-32 (ADR-030 §3) y solo ve los catálogos de
  // finanzas: categorías de gasto, clientes y tarifas.
  { label: 'Administración', icon: <ShieldCheck size={20} />,     to: NAV_ROUTES.ADMIN,      roles: ['admin', 'supervisor', 'executive'] },
];
